import LabelRow from "@components/rowcomponents/LabelRow";
import TruncatedCopyValue from "@components/rowcomponents/TruncatedCopyValue";

export function formatFieldLabel(key: string) {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

export function isPlainObject(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function stringifyObject(value: unknown) {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function formatPrimitive(value: unknown): string {
  if (value == null || value === "") return "- -";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function hasCopyablePrimitive(value: unknown) {
  return value != null && String(value).trim() !== "";
}

type ObjectKeyValueRowsProps = {
  data: Record<string, unknown>;
  includeRootRaw?: boolean;
};

const ObjectKeyValueRows = ({
  data,
  includeRootRaw = false,
}: ObjectKeyValueRowsProps) => {
  const entries = Object.entries(data);

  if (entries.length === 0) {
    return (
      <span className="text-text-base text-[12px] font-normal">
        No data available
      </span>
    );
  }

  return (
    <>
      {entries.map(([key, value]) => {
        if (isPlainObject(value)) {
          const nestedEntries = Object.entries(value);
          return (
            <div key={key} className="flex flex-col gap-2">
              <span className="text-text-base text-[12px] font-semibold">
                {formatFieldLabel(key)}
              </span>
              {nestedEntries.map(([nestedKey, nestedValue]) =>
                isPlainObject(nestedValue) || Array.isArray(nestedValue) ? (
                  <LabelRow
                    key={`${key}.${nestedKey}`}
                    label={formatFieldLabel(nestedKey)}
                    valueComp={
                      <TruncatedCopyValue
                        value={stringifyObject(nestedValue)}
                        copyMessage={`${formatFieldLabel(nestedKey)} copied to clipboard`}
                      />
                    }
                  />
                ) : (
                  <LabelRow
                    key={`${key}.${nestedKey}`}
                    label={formatFieldLabel(nestedKey)}
                    value={formatPrimitive(nestedValue)}
                    isCopy={hasCopyablePrimitive(nestedValue)}
                  />
                ),
              )}
              <LabelRow
                label="Raw object"
                valueComp={
                  <TruncatedCopyValue
                    value={stringifyObject(value)}
                    copyMessage={`${formatFieldLabel(key)} copied to clipboard`}
                  />
                }
              />
            </div>
          );
        }

        if (Array.isArray(value)) {
          return (
            <LabelRow
              key={key}
              label={formatFieldLabel(key)}
              valueComp={
                <TruncatedCopyValue
                  value={stringifyObject(value)}
                  copyMessage={`${formatFieldLabel(key)} copied to clipboard`}
                />
              }
            />
          );
        }

        return (
          <LabelRow
            key={key}
            label={formatFieldLabel(key)}
            value={formatPrimitive(value)}
            isCopy={hasCopyablePrimitive(value)}
          />
        );
      })}
      {includeRootRaw && (
        <LabelRow
          label="Raw object"
          valueComp={
            <TruncatedCopyValue
              value={stringifyObject(data)}
              copyMessage="Object copied to clipboard"
            />
          }
        />
      )}
    </>
  );
};

export default ObjectKeyValueRows;
